<template>
  <div class="ps" @keydown.esc="close">
    <div class="ps-field" :class="{ 'is-open': open }">
      <svg class="ps-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
      <input
        ref="input"
        class="ps-input"
        type="search"
        v-model.trim="query"
        placeholder="Find a page…"
        autocomplete="off"
        @focus="open = true"
        @keydown.enter.prevent="pickFirst"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
      />
      <kbd class="ps-kbd" v-if="!query">⌘K</kbd>
    </div>

    <div class="ps-drop" v-if="open && query">
      <div v-if="!results.length" class="ps-empty">
        Nothing matches “{{ query }}”. Try a word from the page name or what it shows — “sales”, “coupon”, “store”…
      </div>
      <button
        v-for="(r, i) in results"
        :key="r.name"
        type="button"
        class="ps-row"
        :class="{ 'is-hi': i === cursor }"
        @mouseenter="cursor = i"
        @click="pick(r.name)"
      >
        <span class="ps-row-title">{{ $tr(r.title) }}</span>
        <span class="ps-row-path">{{ r.path }}</span>
        <span class="ps-row-note" v-if="r.note">{{ $tr(r.note) }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import pages from "@/router/routes";
import { visibleMenu, searchIndex } from "@/menu";
import { tr } from "@/i18n";

// Lower case for matching; "İ" lower-cases with a combining dot, which is dropped.
const low = (s) => String(s || "").toLowerCase().replace(/\u0307/g, "");

const ROUTE_NAMES = new Set(pages.map((r) => r.name));

/**
 * The page finder, in the top bar. ⌘K / Ctrl+K focuses it from anywhere; typing
 * lists matching pages (name, group, what it shows), Enter opens the first,
 * arrows move, Esc clears. Only pages this person may open are listed.
 */
export default {
  name: "PageSearch",
  data() {
    return { query: "", open: false, cursor: 0 };
  },
  computed: {
    ...mapState({ stateAuth: "auth", perms: (s) => s.rbac.me.permissions }),
    hiddenPages() {
      return (import.meta.env.VUE_APP_HIDDEN_PAGES || "").split(",").map((s) => s.trim()).filter(Boolean);
    },
    results() {
      const q = this.query.toLowerCase();
      if (!q) return [];
      const words = q.split(/\s+/).filter(Boolean);
      const menu = visibleMenu(this.stateAuth.current?.user?.utype, this.hiddenPages, this.perms, {
        allStores: this.$store.getters.isAllStores,
      });
      const rank = (r) => {
        const title = r.title.toLowerCase();
        // The title as shown (Turkish when the panel is in Turkish) is searched as well as the English one.
        const shown = low(tr(r.title));
        const hay = title + " " + shown + " " + r.path.toLowerCase() + " " + r.note.toLowerCase() + " " + low(tr(r.note));
        if (!words.every((w) => hay.includes(w))) return -1;
        if (title.startsWith(q) || shown.startsWith(q)) return 0;
        if (words.every((w) => title.includes(w))) return 1;
        if (words.every((w) => (title + " " + r.path.toLowerCase()).includes(w))) return 2;
        return 3;
      };
      return searchIndex(menu)
        .filter((r) => ROUTE_NAMES.has(r.name))
        .map((r) => ({ ...r, rank: rank(r) }))
        .filter((r) => r.rank >= 0)
        .sort((a, b) => a.rank - b.rank || a.title.localeCompare(b.title))
        .slice(0, 12);
    },
  },
  watch: {
    query() { this.cursor = 0; },
  },
  created() {
    this._onKey = (e) => {
      const k = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === "k") {
        e.preventDefault();
        this.$refs.input && this.$refs.input.focus();
        this.open = true;
      }
    };
    this._onDoc = (e) => { if (!this.$el.contains(e.target)) this.open = false; };
    window.addEventListener("keydown", this._onKey);
    document.addEventListener("click", this._onDoc);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this._onKey);
    document.removeEventListener("click", this._onDoc);
  },
  methods: {
    move(d) {
      if (!this.results.length) return;
      this.cursor = (this.cursor + d + this.results.length) % this.results.length;
    },
    pick(name) {
      this.query = "";
      this.open = false;
      this.$refs.input && this.$refs.input.blur();
      if (this.$route.name !== name) this.$router.push({ name }).catch(() => {});
    },
    pickFirst() {
      if (this.results.length) this.pick(this.results[this.cursor] ? this.results[this.cursor].name : this.results[0].name);
    },
    close() {
      this.query = "";
      this.open = false;
      this.$refs.input && this.$refs.input.blur();
    },
  },
};
</script>

<style scoped>
.ps { position: relative; width: 100%; max-width: 30rem; }
.ps-field { display: flex; align-items: center; gap: var(--s-2); height: 36px; padding: 0 var(--s-3); border: 1px solid var(--border-primary); border-radius: var(--r-md); background: var(--bg-input); transition: border-color var(--motion-fast) var(--ease-out), box-shadow var(--motion-fast) var(--ease-out); }
.ps-field.is-open, .ps-field:focus-within { border-color: var(--border-focus); box-shadow: 0 0 0 3px var(--accent-primary-bg); }
.ps-icon { width: 16px; height: 16px; color: var(--text-muted); flex: 0 0 auto; }
.ps-input { flex: 1; min-width: 0; border: 0; background: transparent; color: var(--text-primary); font-size: var(--t-body-sm); font-family: inherit; outline: none; }
.ps-input::placeholder { color: var(--text-muted); }
.ps-input::-webkit-search-cancel-button { -webkit-appearance: none; }
.ps-kbd { font-size: 11px; line-height: 1; padding: 3px 6px; border: 1px solid var(--border-primary); border-radius: var(--r-sm); color: var(--text-muted); background: var(--bg-primary); font-family: inherit; }
.ps-drop { position: absolute; top: calc(100% + 6px); left: 0; right: 0; z-index: 60; max-height: 60vh; overflow-y: auto; padding: var(--s-1); background: var(--bg-primary); border: 1px solid var(--border-primary); border-radius: var(--r-lg); box-shadow: var(--shadow-overlay); }
.ps-empty { padding: var(--s-3) var(--s-4); font-size: var(--t-body-sm); line-height: var(--t-body-sm-lh); color: var(--text-muted); }
.ps-row { display: block; width: 100%; text-align: left; padding: var(--s-2) var(--s-3); border: 0; border-radius: var(--r-md); background: transparent; cursor: pointer; font-family: inherit; }
.ps-row.is-hi { background: var(--sidebar-active-bg); }
.ps-row-title { display: block; font-size: var(--t-body); line-height: var(--t-body-lh); font-weight: 500; color: var(--text-primary); }
.ps-row.is-hi .ps-row-title { color: var(--sidebar-active-text); }
.ps-row-path { display: block; font-size: var(--t-caption); line-height: var(--t-caption-lh); color: var(--text-muted); }
.ps-row-note { display: block; font-size: var(--t-caption); line-height: var(--t-caption-lh); color: var(--text-secondary); margin-top: 2px; }
</style>
